import type { ManualChapter } from '../types';

/** Rozdział instrukcji: Trade (28.10.2026, S-28.10-02). */
export const chapter: ManualChapter = {
  key: 'trade',
  title: 'Trade',
  audience:
    'Dla traderów i Team Leadera Trade — osób, które sprzedają zlecenia przewoźnikom: przez giełdy Timocom i Trans.eu albo bezpośrednio.',
  intro:
    'Trade przejmuje zlecenia, których nie obsłuży flota własna. Trader wystawia je na giełdę, szuka przewoźników z historią na relacji, przygotowuje zapytania, zapisuje oferty i wybiera przewoźnika. Aplikacja przygotowuje dane i pilnuje bramek, ale nic nie wystawia ani nie wysyła sama.',
  sections: [
    {
      id: 'rola',
      title: 'Praca w dziale Trade',
      blocks: [
        {
          kind: 'text',
          text: 'Zlecenia Trade widzisz na liście zleceń w filtrze „Widok działu” → „Trade — przewoźnicy i giełdy” (dla konta tradera to widok domyślny). Na karcie zlecenia masz sekcje: „Publikacje na giełdach”, „Przewoźnicy z historią na tej relacji” i „Zapytanie do przewoźnika”. Warianty floty i obsadzony pojazd widzisz tylko do odczytu.',
        },
        {
          kind: 'terms',
          title: 'Stany zlecenia w Trade',
          items: [
            { term: 'Przekazane do Trade', meaning: 'Zlecenie prowadzi Trade — szukasz przewoźnika.' },
            { term: 'Sprzedane przez Trade', meaning: 'W TMS pojawił się przewoźnik albo numer pojazdu przy zleceniu prowadzonym przez Trade. Ustawia to wyłącznie import z TMS — ręcznie tego stanu nie wybierzesz. Ze stanu „Sprzedane” zlecenie nie wraca do FTL.' },
            { term: 'Obsadzone przewoźnikiem', meaning: 'TMS ma przewoźnika przy zleceniu, które nie przeszło przez Trade (podwykonawstwo). Ustawia to import.' },
            { term: 'Nieaktualne', meaning: 'Zlecenie wyszło z planowania (np. anulowane).' },
          ],
        },
        {
          kind: 'text',
          when: { on: ['carrierAssignedInTrade'] },
          text: 'Stan „Obsadzone przewoźnikiem” należy do Trade: jest w widoku Trade, a przejście do niego z importu trafia do Twojego dzwonka. Giełdy i zapytania przy takim zleceniu są niedostępne — przewoźnik już jest.',
        },
        {
          kind: 'text',
          when: { off: ['carrierAssignedInTrade'] },
          text: 'Stan „Obsadzone przewoźnikiem” jest w widoku FTL.',
        },
        {
          kind: 'list',
          title: 'Kto co może',
          items: [
            'Trader — przekazuje i zwraca zlecenia, wystawia i wycofuje oferty na giełdach, prowadzi kartotekę przewoźników, zapytania i oferty.',
            'Team Leader Trade — wszystko to, co trader, a do tego zgoda działu Trade tam, gdzie zmiana wymaga przełożonego (niżej). Wniosków o wyjątek od zakazu podwykonawcy domyślnie nie akceptuje — to uprawnienie administratorów.',
            'Planista FTL — widzi stan publikacji na karcie zlecenia, ale nie ma przycisków giełd.',
          ],
        },
        {
          kind: 'text',
          when: { on: ['orderChangeLeadAfterDeparture'] },
          text: 'Zgoda Team Leadera Trade: gdy auto wyruszyło już na załadunek, zmianę danych zlecenia realizowanego przez Trade w polach, które należą do Trade, zatwierdza Team Leader Trade (albo administrator). Trader takiej zmiany nie zatwierdzi.',
        },
        {
          kind: 'text',
          when: { on: ['inquiryQuotes'] },
          text: 'Cenę wyceny dla klienta („Zatwierdź cenę”) zatwierdza trader albo Team Leader Trade.',
        },
        {
          kind: 'note',
          tone: 'human',
          text: 'System przygotowuje, człowiek zatwierdza. Na giełdę wystawia człowiek, zapytanie do przewoźnika wysyłasz Ty ze swojej poczty, przewoźnika wpisujesz w TMS sam. Aplikacja nic nie wysyła sama i nic nie zapisuje w TMS.',
        },
      ],
    },
    {
      id: 'pulpit-trade',
      title: 'Pulpit Trade',
      menu: ['trade-dashboard'],
      when: { on: ['tradeDashboard'] },
      blocks: [
        {
          kind: 'text',
          text: 'Pozycja menu „Pulpit Trade” pokazuje liczby, od których zaczynasz dzień. Każda liczba otwiera listę zleceń z tym samym filtrem, więc liczba i lista się zgadzają. Kafelki pojawiają się tylko przy funkcjach, które są włączone.',
        },
        {
          kind: 'image',
          image: { guide: 'tradeDashboard', step: 1 },
          caption: 'Pulpit Trade z kafelkami zleceń.',
        },
        {
          kind: 'terms',
          title: 'Kafelki pulpitu',
          items: [
            { term: 'Przekazane do Trade', meaning: 'Wszystkie zlecenia, które prowadzisz.' },
            { term: 'Z ofertami przewoźników / Bez ofert przewoźników', meaning: 'Czy przy zleceniu zapisano już ofertę przewoźnika.', when: { on: ['carrierOfferRegister'] } },
            { term: 'Przewoźnik zaakceptowany — czeka na TMS', meaning: 'Przewoźnik wybrany w aplikacji, ale jeszcze nie wpisany w TMS.', when: { on: ['carrierAcceptance'] } },
            { term: 'Jutro załadunek, bez przewoźnika', meaning: 'Najpilniejsze — załadunek jutro.' },
            { term: 'Na giełdzie', meaning: 'Zlecenia z ofertą wystawioną na giełdzie.' },
            { term: 'Do sprzedaży', meaning: 'Zlecenia STANDARD i MEGA w planowaniu FTL — rekomendacja, przekazuje człowiek.', when: { on: ['saleRecommendation'] } },
            { term: 'Zwrócone do FTL', meaning: 'Zlecenia, które Trade oddał flocie.' },
            { term: 'Obsadzone przewoźnikiem', meaning: 'Zlecenia z przewoźnikiem z TMS.', when: { on: ['carrierAssignedInTrade'] } },
          ],
        },
        {
          kind: 'task',
          title: 'Zacznij dzień od pulpitu',
          steps: [
            'Otwórz „Pulpit Trade” w menu.',
            'Kliknij „Jutro załadunek, bez przewoźnika” i zajmij się tymi zleceniami najpierw.',
            'Kliknij „Przekazane do Trade” — to Twoja bieżąca lista pracy.',
          ],
        },
      ],
    },
    {
      id: 'przekazanie-i-zwrot',
      title: 'Przekazanie do Trade i zwrot do FTL',
      blocks: [
        {
          kind: 'text',
          text: 'Zlecenie bez pojazdu (stany „Gotowe do planowania” i „Wstępnie planowane”) może przekazać do Trade każdy pracownik. Zlecenie obsadzone pojazdem floty przekazuje Team Leader FTL albo administrator (wyjątki — niżej). Zwrot z Trade do FTL może wykonać każdy, ale zawsze z powodem.',
        },
        {
          kind: 'task',
          title: 'Przekazać zlecenie do Trade',
          steps: [
            'Otwórz kartę zlecenia.',
            'W sekcji „Akcje” w polu „Zmień stan” wybierz „Przekazane do Trade”.',
            'Wpisz albo wybierz powód w polu obok. Gdy pole jest oznaczone „(wymagany)”, bez powodu zmiana nie przejdzie.',
            'Kliknij „Zmień”.',
          ],
          outcome: 'Zlecenie trafia do widoku Trade, a dział Trade dostaje powiadomienie w dzwonku.',
        },
        {
          kind: 'task',
          title: 'Zwrócić zlecenie do FTL z powodem',
          intro: 'Gdy zlecenia nie da się sprzedać albo flota jednak je obsłuży — zanim zlecenie zostanie sprzedane.',
          steps: [
            'Otwórz kartę zlecenia w stanie „Przekazane do Trade”.',
            'W polu „Zmień stan” wybierz stan działu FTL (zwykle „Gotowe do planowania”).',
            'W polu „Powód (wymagany)” wpisz powód zwrotu.',
            'Kliknij „Zmień”.',
          ],
          outcome: 'Wystawione oferty schodzą z giełdy same w ciągu minuty, a FTL dostaje powiadomienie.',
        },
        {
          kind: 'text',
          when: { on: ['tradeTransferReasons'] },
          text: 'Powód przekazania i zwrotu wybierasz z listy — w polu „Powód przekazania do Trade” albo „Powód zwrotu do FTL (wymagany)” — a w polu „Komentarz (opcjonalny)” możesz go uzupełnić. Przy powodzie, który wymaga opisu (np. „inny”), pole zmienia się w „Komentarz (wymagany)”. Powód widać na karcie zlecenia, pod stanem na liście i w filtrze „Powód przekazania/zwrotu”. Listę powodów prowadzi administrator.',
        },
        {
          kind: 'image',
          when: { on: ['tradeTransferReasons'] },
          image: { guide: 'tradeTransferReasons', step: 2 },
          caption: 'Lista powodów zwrotu do FTL.',
        },
        {
          kind: 'text',
          when: { on: ['tradeHandoverReasonOptional'] },
          text: 'Przy przekazaniu obsadzonego zlecenia powód jest opcjonalny. Zwrot do FTL i wniosek o przekazanie po wyjeździe pojazdu dalej wymagają powodu.',
        },
        {
          kind: 'text',
          when: { on: ['tradeTransferBeforeDeparture'] },
          text: 'Planista FTL sam przekazuje do Trade zlecenie obsadzone swoim pojazdem, dopóki pojazd nie wyruszył na załadunek („Przekaż do Trade”). Po wyjeździe składa wniosek, który rozpatruje Team Leader FTL.',
        },
        {
          kind: 'text',
          when: { on: ['tradeDualTrack'] },
          text: 'Praca dwutorowa: przekazanie do Trade nie zwalnia wariantów floty. FTL dalej je dodaje, a zlecenie w Trade może obsadzić flota — wtedy oferta schodzi z giełdy, a zapytania do przewoźników są oznaczone jako „nieaktualne”. Gdy sprzedasz zlecenie (przewoźnik w TMS), warianty floty zwalniają się z powodem. Na karcie widać, kto prowadzi zlecenie.',
        },
        {
          kind: 'text',
          when: { off: ['tradeDualTrack'] },
          text: 'Przekazanie do Trade zwalnia warianty floty, a zlecenia w Trade flota nie obsadza.',
        },
        {
          kind: 'note',
          tone: 'system',
          when: { on: ['saleRecommendation'] },
          text: 'Zlecenia z wymaganym typem STANDARD TRUCK albo MEGA TRUCK w planowaniu FTL dostają znacznik „Do sprzedaży” i komunikat na karcie; filtr „Znaczniki” ma opcję „Do sprzedaży”. To rekomendacja, nie automat — do Trade przekazuje człowiek.',
        },
        {
          kind: 'text',
          when: { off: ['exchangeBothCompanies'] },
          text: 'Zleceń spółki bez kont giełdowych nie przekazuje się do Trade i nie wystawia na giełdy — opcja jest wyszarzona z powodem. Takie zlecenia zostają w planowaniu floty.',
        },
        {
          kind: 'text',
          when: { on: ['exchangeBothCompanies'] },
          text: 'Spółka bez własnych kont giełdowych może mieć wskazaną spółkę, z której kont publikujemy jej zlecenia. Wtedy jej zlecenia przekazujesz do Trade i wystawiasz normalnie — oferta idzie z konta wskazanej spółki. Bez wskazania blokada zostaje.',
        },
      ],
    },
    {
      id: 'lista-zlecen',
      title: 'Lista zleceń w Trade',
      blocks: [
        {
          kind: 'image',
          image: { shot: 'bank' },
          caption: 'Lista zleceń — filtry „Widok działu”, „Stan planowania” i „Znaczniki”.',
        },
        {
          kind: 'text',
          when: { on: ['sourcePreview'] },
          text: 'Co dokładnie zamówił klient: na karcie prezlecenia obok danych stoi mail klienta i jego załączniki (otwiera je przycisk „Pokaż źródło”, przy prezleceniach z dokumentów są od razu), każdy w osobnej zakładce. PDF i zdjęcie zobaczysz na miejscu, arkusz i dokument pobierzesz przyciskiem „Pobierz oryginał”. Źródło jest tylko do odczytu — poprawki danych wprowadza CS albo Administracja.',
        },
        {
          kind: 'terms',
          title: 'Filtry przydatne w Trade',
          items: [
            { term: 'Tylko wystawione na giełdzie', meaning: 'Zlecenia z ofertą na giełdzie, także te czekające w kolejce.', when: { off: ['postedOnExchangeFilter'] } },
            { term: 'Tylko wystawione na giełdzie', meaning: 'Tylko oferty przyjęte przez giełdę, bez czekających w kolejce.', when: { on: ['postedOnExchangeFilter'] } },
            { term: 'Na giełdzie lub w kolejce', meaning: 'Oferty przyjęte przez giełdę i czekające na wysłanie.', when: { on: ['postedOnExchangeFilter'] } },
            { term: 'Z otwartą ofertą przewoźnika', meaning: 'Zlecenia, przy których czeka zapisana oferta.', when: { on: ['carrierOfferRegister'] } },
            { term: 'Tylko zwrócone z Trade', meaning: 'Zlecenia oddane flocie.', when: { on: ['tradeDashboard'] } },
            { term: 'Powód przekazania/zwrotu', meaning: 'Zlecenia z wybranym powodem z listy.', when: { on: ['tradeTransferReasons'] } },
          ],
        },
        {
          kind: 'text',
          when: { off: ['exchangeColumn'] },
          text: 'Stan oferty na giełdzie widać jako znacznik w kolumnie „Znaczniki”, np. „TIMOCOM: Wystawiona”.',
        },
        {
          kind: 'text',
          when: { on: ['exchangeColumn'] },
          text: 'Stan oferty na giełdzie ma własną kolumnę „Giełda”: giełda z ikoną stanu i „od kiedy” (np. „wystawiona od 09:27”), w dymku pełne zdanie, a przy nieudanej próbie powód. Kolumna jest też w eksporcie.',
        },
      ],
    },
    {
      id: 'wystawienie-na-gielde',
      title: 'Wystawienie zlecenia na giełdę',
      blocks: [
        {
          kind: 'text',
          text: 'Zlecenie wystawiasz z karty zlecenia, w sekcji „Publikacje na giełdach”, przyciskiem „Wystaw na TIMOCOM” albo „Wystaw na Trans.eu”. Na giełdę trafia oferta frachtu: punkty trasy, terminy, masa i długość ładunku — bez ceny i bez uwag wewnętrznych. Dokładny adres nie idzie na giełdę nigdy. Gdy przycisk jest nieaktywny, obok stoi powód.',
        },
        {
          kind: 'terms',
          title: 'Bramka 1: czy klient dopuszcza podwykonawców',
          items: [
            { term: 'Klient dopuszcza podwykonawców', meaning: 'Możesz wystawiać bez dodatkowego potwierdzenia.' },
            { term: 'Dopuszcza warunkowo', meaning: 'Zaznacz w oknie, że warunki klienta są spełnione.' },
            { term: 'Nie ustalono', meaning: 'Reguły nie wpisano w kartotece kontrahenta. Brak reguły to nie zgoda — zaznacz ręczne potwierdzenie.' },
            { term: 'Zakazuje przekazywania podwykonawcom', meaning: 'Publikacja tylko jako wyjątek udzielony przez uprawnioną osobę, z powodem.' },
          ],
        },
        {
          kind: 'terms',
          title: 'Bramka 2: jak pokazać lokalizację na giełdzie',
          items: [
            { term: 'Skrócony kod pocztowy', meaning: 'Miasto i dwie pierwsze cyfry kodu.' },
            { term: 'Tylko miasto', meaning: 'Miasto bez kodu pocztowego.' },
            {
              term: 'Przybliżony',
              meaning: 'Pełny kod pocztowy innej miejscowości w pobliżu (do kilkudziesięciu kilometrów) — nigdy faktyczny adres. Bez wyniku publikacja w tym trybie jest odmawiana z powodem.',
              when: { on: ['maskingNearbyLocality'] },
            },
          ],
        },
        {
          kind: 'task',
          title: 'Wystawić zlecenie na giełdę',
          steps: [
            'Otwórz kartę zlecenia w stanie „Przekazane do Trade” i przejdź do sekcji „Publikacje na giełdach”.',
            'Przeczytaj zdanie o regule klienta i lokalizacji nad przyciskami, potem kliknij „Wystaw na TIMOCOM” (albo „Wystaw na Trans.eu”).',
            'W oknie zaznacz potwierdzenie podwykonawstwa, jeśli okno go wymaga.',
            'Jeśli spółka nie ma ustawionego trybu, wybierz „Lokalizacja na giełdzie”.',
            'Sprawdź podgląd „Tak zobaczy to przewoźnik” i wybór pojazdu.',
            'Opcjonalnie wpisz powód (zapisze się w historii zlecenia) i kliknij „Wystaw”.',
          ],
          outcome: '„Zlecono publikację — system spróbuje wystawić ofertę w ciągu minuty.” Wynik zobaczysz w tabeli sekcji: stan „Wystawiona” albo „Nieudana” z powodem.',
        },
        {
          kind: 'text',
          when: { on: ['publicationDataConfirmation'] },
          text: 'W oknie jest krok „Dane do publikacji — sprawdź i potwierdź”: typ pojazdu i zabudowa, długość ładunku, wysokość i wózek widłowy. Wartość z typu zlecenia to podpowiedź do potwierdzenia, nie wartość domyślna; przy zleceniu bez typu okno mówi to wprost i podpowiada domyślny typ floty (JUMBO) do sprawdzenia. Bez zaznaczenia „Potwierdzam dane do publikacji” przycisk „Wystaw” jest nieaktywny.',
        },
        {
          kind: 'image',
          when: { on: ['publicationDataConfirmation'] },
          image: { guide: 'publicationDataConfirmation', step: 1 },
          caption: 'Okno publikacji — tabela „Dane do publikacji”.',
        },
        {
          kind: 'note',
          tone: 'warning',
          when: { on: ['publicationFleetVariantsWarning'] },
          text: 'Gdy zlecenie ma warianty floty, sekcja publikacji, okno i kolejka pokazują „Zlecenie ma warianty floty: …” z autorem wariantu. Przed publikacją porozmawiaj z planistą. To ostrzeżenie, nie blokada — zapis publikacji mówi, że je widziałeś.',
        },
        {
          kind: 'text',
          when: { on: ['exchangePersonalAccount'] },
          text: 'Oferta idzie z Twojego imiennego konta giełdowego. Dane logowania wpisujesz w menu użytkownika „Moje konta giełdowe” (tylko zapis — wartości nie da się odczytać). Bez Twojego konta przycisk giełdy jest nieaktywny z powodem.',
        },
        {
          kind: 'text',
          when: { off: ['exchangePersonalAccount'] },
          text: 'Oferta idzie z konta giełdowego spółki. Osobą kontaktową w ofercie Timocom jesteś Ty — z imienia i nazwiska w Twoim profilu.',
        },
        {
          kind: 'terms',
          title: 'Stany publikacji',
          items: [
            { term: 'Oczekująca', meaning: 'Zlecona, czeka na wysłanie (do minuty). Możesz ją „Anuluj”.' },
            { term: 'Wystawiona', meaning: 'Giełda przyjęła ofertę.' },
            { term: 'Nieudana', meaning: 'Giełda odmówiła albo wystąpił błąd — powód w kolumnie „Wynik”. Możesz kliknąć „Ponów”.' },
            { term: 'Wycofana', meaning: 'Oferta zdjęta z giełdy.' },
            { term: 'Anulowana', meaning: 'Publikację anulowano, zanim poszła na giełdę.' },
          ],
        },
        {
          kind: 'note',
          tone: 'warning',
          text: 'Trans.eu nie ma środowiska testowego — każda oferta jest prawdziwa. Gdy w kolejce publikacji przy kontach Trans.eu widać „wystawianie zablokowane”, na Trans.eu nie wystawisz; wycofywanie działa zawsze.',
        },
      ],
    },
    {
      id: 'wycofanie-oferty',
      title: 'Wycofanie oferty z giełdy',
      blocks: [
        {
          kind: 'task',
          title: 'Wycofać ofertę z giełdy',
          steps: [
            'Otwórz kartę zlecenia i przejdź do sekcji „Publikacje na giełdach”.',
            'Przy publikacji w stanie „Wystawiona” kliknij „Wycofaj”.',
            'Sprawdź komunikat: „Wycofano ofertę … z giełdy” i stan „Wycofana”.',
          ],
          outcome: 'Gdy giełda odmówi wycofania, pojawi się „Wycofaj mimo błędu” — oznacza ofertę jako wycofaną w aplikacji, ale oferta może nadal być widoczna na giełdzie. Sprawdź ją wtedy na giełdzie.',
        },
        {
          kind: 'note',
          tone: 'system',
          text: 'Oferta schodzi z giełdy sama w ciągu minuty, gdy zlecenie przestaje być do sprzedania: zostało obsadzone flotą, zwrócone do FTL, przeszło do stanu nieaktywnego albo minął dzień załadunku. Gdy giełda odmówi, oferta zostaje „Wystawiona” z ostrzeżeniem — wtedy wycofaj ją ręcznie.',
        },
      ],
    },
    {
      id: 'kolejka-publikacji',
      title: 'Kolejka publikacji',
      menu: ['postings'],
      blocks: [
        {
          kind: 'text',
          text: '„Kolejka publikacji” (grupa „Giełdy”) to lista wszystkich publikacji ze wszystkich zleceń: stan, giełda, zlecenie, relacja, kto i kiedy zlecił. Filtrujesz po stanie, giełdzie i numerze zlecenia. U góry widać, czy spółki mają komplet danych dostępowych do Timocom i Trans.eu.',
        },
        {
          kind: 'image',
          image: { shot: 'publication-queue' },
          caption: 'Kolejka publikacji z kontami giełdowymi spółek.',
        },
        {
          kind: 'task',
          title: 'Sprawdzić nieudane publikacje',
          steps: [
            'Otwórz „Kolejka publikacji”.',
            'W filtrze „Stan” wybierz „Nieudana”.',
            'Otwórz zlecenie, przeczytaj powód w kolumnie „Wynik” i popraw dane albo kliknij „Ponów”.',
          ],
        },
      ],
    },
    {
      id: 'wnioski-o-wyjatek',
      title: 'Wniosek o wyjątek od bramki publikacji',
      menu: ['publication-exceptions'],
      when: { on: ['publicationExceptionRequests'] },
      blocks: [
        {
          kind: 'text',
          text: 'Gdy klient zakazuje podwykonawców albo nie możesz potwierdzić jego warunków, składasz przy zleceniu wniosek o wyjątek. Wniosek trafia na listę „Wnioski o wyjątek” do osób z uprawnieniem „Akceptacja wyjątku od zakazu podwykonawcy”. Dopiero zatwierdzony wniosek przepuszcza publikację.',
        },
        {
          kind: 'task',
          title: 'Poprosić o wyjątek',
          steps: [
            'Na karcie zlecenia w sekcji publikacji kliknij „Poproś o wyjątek”.',
            'Wpisz powód (co najmniej 10 znaków).',
            'Kliknij „Złóż wniosek”.',
          ],
          outcome: 'Po zatwierdzeniu okno publikacji pokaże numer wniosku i nie będzie wymagać potwierdzenia. Wniosek wygasa, gdy zlecenie zejdzie z Trade albo zmieni się kontrahent lub jego reguła.',
        },
        {
          kind: 'task',
          title: 'Rozpatrzyć wniosek o wyjątek',
          intro: 'Dla osób z uprawnieniem do akceptacji wyjątku.',
          steps: [
            'Otwórz „Wnioski o wyjątek” w grupie „Giełdy” (licznik przy pozycji menu mówi, ile czeka).',
            'Przeczytaj zlecenie, klienta i powód.',
            'Kliknij „Zatwierdź” albo „Odrzuć” i podaj powód.',
          ],
          outcome: 'Własnych wniosków nie rozstrzygasz.',
        },
        {
          kind: 'image',
          image: { guide: 'publicationExceptionRequests', step: 3 },
          caption: 'Lista „Wnioski o wyjątek”.',
        },
      ],
    },
    {
      id: 'wyjatek-bez-wniosku',
      title: 'Wyjątek od zakazu podwykonawców',
      when: { off: ['publicationExceptionRequests'] },
      blocks: [
        {
          kind: 'text',
          text: 'Gdy klient zakazuje podwykonawców, okno publikacji wymaga zaznaczenia „Udzielam wyjątku od zakazu podwykonawstwa dla tego zlecenia” i powodu. Wyjątku udziela osoba z dostępem do sekcji administracyjnej; system zapisze, kto, kiedy i dlaczego go udzielił.',
        },
      ],
    },
    {
      id: 'przewoznicy',
      title: 'Kartoteka przewoźników',
      menu: ['carriers'],
      blocks: [
        {
          kind: 'text',
          text: '„Przewoźnicy” to lista przewoźników z TMS. Z TMS przychodzi numer i nazwa; profil (liczba zleceń, kierunki z ceną za km, pojazdy użyte dla Marathonu) system wylicza z historii zleceń. NIP/VAT, współpracę, notatki i kontakty wpisuje Trade — każda zmiana trafia do dziennika działań.',
        },
        {
          kind: 'image',
          image: { shot: 'carriers' },
          caption: 'Lista przewoźników z liczbą zleceń i współpracą.',
        },
        {
          kind: 'task',
          title: 'Uzupełnić dane przewoźnika',
          steps: [
            'Otwórz „Przewoźnicy” i wyszukaj przewoźnika („Szukaj po nazwie”).',
            'W sekcji „Dane przewoźnika” wpisz NIP/VAT, ustaw „Współpraca” i dopisz notatki.',
            'W sekcji „Kontakty” dodaj osobę: rolę, e-mail, telefon, kanał i opcjonalnie opiekuna po stronie Marathona.',
            'Zapisz zmiany.',
          ],
        },
        {
          kind: 'note',
          tone: 'warning',
          text: 'Pusta „Współpraca” znaczy „nieustalona”, nie „tak”. Wstrzymana albo nieustalona współpraca to przeciwwskazanie widoczne przy zapytaniu i ofercie, nie blokada.',
        },
        {
          kind: 'text',
          when: { on: ['carrierVerification'] },
          text: 'Sekcja „Weryfikacja”: ubezpieczenie OCP (ważne do), licencja lub zezwolenie i VAT czynny (dzień sprawdzenia). Każda pozycja ma stan: potwierdzone, brak, po terminie albo nie wpisano. Status zbiorczy „pełna” jest tylko wtedy, gdy wszystkie trzy są potwierdzone; „nieustalona” to nie zgoda. Wpisujesz po sprawdzeniu dokumentów — „Wpisz” albo „Zmień” przy pozycji.',
        },
        {
          kind: 'image',
          when: { on: ['carrierVerification'] },
          image: { guide: 'carrierVerification', step: 1 },
          caption: 'Sekcja „Weryfikacja” na karcie przewoźnika.',
        },
        {
          kind: 'text',
          when: { on: ['tmsContactsAndCustomerTypes'] },
          text: 'Karta przewoźnika ma też sekcję „Kontakty z TMS” (tylko do odczytu, zmienia się je w TMS) — taki kontakt możesz wybrać na adresata zapytania. Karta kontrahenta pokazuje „Typ klienta (TMS)”, a przy publikacji i zapytaniu stoi podpowiedź z tego typu. To podpowiedź: o podwykonawstwie decyduje reguła wpisana w kartotece kontrahenta.',
        },
      ],
    },
    {
      id: 'przewoznicy-z-historia',
      title: 'Przewoźnicy z historią na relacji',
      blocks: [
        {
          kind: 'text',
          text: 'Sekcja „Przewoźnicy z historią na tej relacji” na karcie zlecenia pokazuje przewoźników, którzy jeździli dla Marathonu na tej samej relacji (kraj → kraj) w ostatnim półroczu. Najpierw są ci z najniższą średnią ceną za km (kolumna „$”), potem pozostali według liczby kursów. Historia kursów nie mówi, czy przewoźnik ma dziś wolne auto.',
        },
        {
          kind: 'task',
          title: 'Znaleźć przewoźnika z historią',
          steps: [
            'Otwórz kartę zlecenia w Trade i przejdź do sekcji „Przewoźnicy z historią na tej relacji”.',
            'Kliknij „Pokaż przewoźników”.',
            'Przy wybranym przewoźniku kliknij „Przygotuj zapytanie”.',
          ],
        },
        {
          kind: 'text',
          when: { on: ['carrierRelationPrecision'] },
          text: 'W polu „Dokładność relacji” wybierasz, jak dokładnie dopasować kursy: „Dokładnie (kod pocztowy albo miejscowość)”, „Region (kraj + 2 cyfry kodu)”, „Szerzej (kraj + 1 cyfra kodu)” albo „Kraj → kraj (wszystkie kursy)”. Kolumna „Dopasowanie” mówi, na jakim poziomie dopasował się najbliższy kurs.',
        },
      ],
    },
    {
      id: 'zapytanie-do-przewoznika',
      title: 'Zapytanie do przewoźnika',
      blocks: [
        {
          kind: 'text',
          text: 'System przygotowuje szkic zapytania przy zleceniu: jeden wzór dla wszystkich przewoźników, zmienia się tylko adresat. Lokalizacje są zamaskowane jak na giełdzie, terminy w czasie polskim, cena nie jest podawana. Podpis to Twoje imię i nazwisko z profilu — gdy ich nie ma, w treści stoi miejsce do uzupełnienia.',
        },
        {
          kind: 'task',
          title: 'Przygotować zapytanie do przewoźnika (szkic wysyłany ręcznie)',
          steps: [
            'W sekcji „Zapytanie do przewoźnika” wybierz „Przewoźnik z kartoteki” (albo kliknij „Przygotuj zapytanie” przy przewoźniku z historią).',
            'Wybierz „Adresat”, „Język” i, jeśli trzeba, „Maskowanie lokalizacji”.',
            'Kliknij „Przygotuj zapytanie do …” — powstanie szkic ze znacznikiem „szkic”.',
            'Sprawdź i popraw temat i treść, kliknij „Zapisz zmiany”.',
            'Kliknij „Wyślij” (otworzy się Twój program pocztowy) albo „Kopiuj” i wyślij wiadomość samodzielnie.',
            'Po wysłaniu zaznacz potwierdzenie podwykonawstwa, jeśli jest wymagane, i kliknij „Oznacz jako wysłane ręcznie”.',
          ],
          outcome: 'Treść zostaje zamrożona, a historia zlecenia zapisuje, do którego przewoźnika i z którego wzoru — bez danych kontaktowych i bez treści.',
        },
        {
          kind: 'note',
          tone: 'human',
          text: 'Aplikacja niczego nie wysyła. „Oznacz jako wysłane ręcznie” to Twoje oświadczenie, że wiadomość poszła z Twojej poczty — odpowiedzi przewoźnika aplikacja nie odczytuje.',
        },
      ],
    },
    {
      id: 'oferty-przewoznikow',
      title: 'Oferty przewoźników i wybór przewoźnika',
      blocks: [
        {
          kind: 'text',
          when: { off: ['carrierOfferRegister'] },
          text: 'Oferty przewoźników prowadzisz poza aplikacją. Wybranego przewoźnika wpisujesz w TMS — import ustawi wtedy „Sprzedane przez Trade”.',
        },
        {
          kind: 'text',
          when: { on: ['carrierOfferRegister'] },
          text: 'Na karcie zlecenia w Trade sekcja „Oferty przewoźników” to rejestr ofert z telefonu, maila albo giełdy. Przy każdej ofercie stoi marża wobec ceny zlecenia (kurs NBP z dnia załadunku). Nic stąd nie wychodzi do przewoźnika ani na giełdę i wpis nie zmienia stanu zlecenia.',
        },
        {
          kind: 'task',
          title: 'Zapisać ofertę przewoźnika',
          when: { on: ['carrierOfferRegister'] },
          steps: [
            'Otwórz zlecenie w Trade i przejdź do sekcji „Oferty przewoźników”.',
            'Wybierz „Przewoźnik z kartoteki” i „Rodzaj” („Oferta przewoźnika” albo „Kontroferta tradera”).',
            'Wpisz „Kwota”, „Waluta”, „Ważna do (czas polski)” i „Źródło”, opcjonalnie uwagi.',
            'Kliknij „Zapisz ofertę”.',
          ],
          outcome: 'Ofertę, która nie wchodzi w grę, odrzucasz przyciskiem „Odrzuć” z powodem.',
        },
        {
          kind: 'task',
          title: 'Zaakceptować przewoźnika i stawkę',
          when: { on: ['carrierAcceptance'] },
          steps: [
            'W „Ofertach przewoźników” przy wybranej ofercie kliknij „Akceptuj przewoźnika i stawkę”.',
            'Przeczytaj w oknie stawkę, marżę i ostrzeżenia (strata, oferta po terminie ważności, wstrzymana współpraca).',
            'Wpisz warunki (opcjonalnie) i uzasadnienie — wymagane, gdy okno ostrzega.',
            'Kliknij „Akceptuję przewoźnika, stawkę i warunki”.',
            'Wpisz przewoźnika w TMS.',
          ],
          outcome: 'Pozostałe otwarte oferty zostają odrzucone, oferty na giełdach schodzą same w ciągu minuty, a nowej publikacji nie da się zlecić. Stan zlecenia się nie zmienia — „Sprzedane przez Trade” ustawi import, gdy przewoźnik pojawi się w TMS. Pomyłkę cofasz przyciskiem „Cofnij akceptację” z powodem.',
        },
        {
          kind: 'image',
          when: { on: ['carrierAcceptance'] },
          image: { guide: 'carrierAcceptance', step: 1 },
          caption: 'Przycisk „Akceptuj przewoźnika i stawkę” przy ofercie.',
        },
        {
          kind: 'note',
          tone: 'warning',
          when: { on: ['carrierAcceptance', 'carrierVerification'] },
          text: 'Przewoźnika z niepełną albo nieustaloną weryfikacją zaakceptujesz tylko z uzasadnieniem (co najmniej 10 znaków). Historia zlecenia dostaje wtedy wpis „akceptacja mimo niepełnej weryfikacji”.',
        },
        {
          kind: 'terms',
          title: 'Pasma marży',
          when: { on: ['marginFiveBands'] },
          items: [
            { term: 'strata krytyczna', meaning: 'Poniżej −50% kosztu.' },
            { term: 'strata', meaning: 'Od −50% do 0%.' },
            { term: 'marża minimalna', meaning: 'Od 0% do 10%.' },
            { term: 'marża normalna', meaning: 'Od 10% do 70%.' },
            { term: 'do weryfikacji', meaning: 'Powyżej 70% — sprawdź, czy cena i koszt są poprawne.' },
          ],
        },
        {
          kind: 'text',
          when: { on: ['marginFiveBands'] },
          text: 'W Trade pasmo wyznacza procent marży wobec stawki przewoźnika (kosztu frachtu) — ten procent i granice pasma pokazuje dymek przy paśmie. Pasmo to tylko oznaczenie: niczego nie blokuje i nie zmienia kolejności ofert. Granice zmienia administrator.',
        },
        {
          kind: 'text',
          when: { off: ['marginFiveBands'] },
          text: 'Pasmo marży pokazuje się tylko wtedy, gdy administrator wpisze progi alertu — bez progów marża stoi bez oznaczenia.',
        },
      ],
    },
    {
      id: 'alerty-transporeon',
      title: 'Alerty z Transporeonu',
      when: { on: ['transporeonImport'] },
      blocks: [
        {
          kind: 'text',
          text: 'Przy zleceniu w Trade alerty ze źródłem Transporeon przychodzą także do dzwonka Trade: zmiana okna, miejsca, trasy albo wagi, anulacja i ponowne wystawienie pod tym samym numerem. Baner na karcie zlecenia jest ten sam co przy zmianach z TMS; „Przyjmuję do wiadomości” tylko zapisuje, że widziałeś.',
        },
        {
          kind: 'note',
          tone: 'system',
          text: 'Do Transporeonu nic nie wysyłamy — ani ofert, ani cen, ani statusów. Integracja tylko czyta komunikaty zleceniodawców.',
        },
      ],
    },
    {
      id: 'sprzedane',
      title: 'Kiedy zlecenie jest sprzedane',
      blocks: [
        {
          kind: 'text',
          text: 'Giełda nie oznacza sprzedaży — umowa z przewoźnikiem zapada poza nią. Przewoźnik podaje numery rejestracyjne, a Ty wpisujesz przewoźnika i pojazd w TMS. Import z TMS przestawia wtedy zlecenie prowadzone przez Trade na „Sprzedane przez Trade”.',
        },
        {
          kind: 'task',
          title: 'Domknąć sprzedaż',
          steps: [
            'Wpisz w TMS przewoźnika i numer pojazdu przy zleceniu.',
            'Poczekaj na najbliższy import z TMS.',
            'Sprawdź na liście zleceń, że stan zmienił się na „Sprzedane przez Trade”.',
          ],
          outcome: 'Jeśli giełda mówi „wystawione”, a TMS nie zna wykonawcy, rozbieżność trafia do raportu importu — system jej sam nie rozstrzyga.',
        },
        {
          kind: 'note',
          tone: 'system',
          text: 'Stanu „Sprzedane przez Trade” nie ustawia się ręcznie. Sprzedane zlecenie nie wraca do FTL — wychodzi tylko do archiwum.',
        },
      ],
    },
  ],
};
